import { type ForwardedRef, forwardRef, memo, type ReactElement } from 'react';
import ReactSelect, {
  components,
  type GroupBase,
  type Props as ReactSelectProps,
  type SelectInstance,
} from 'react-select';

import type { SelectOption } from '@didaskill/types/web';

import {
  type CommonSelectProps,
  LOADING_MESSAGE,
  NO_OPTIONS_MESSAGE,
  PLACEHOLDER_DEFAULT_VALUE,
  SELECT_SIZES,
} from './constants';
import { useReactSelectCustomization } from './utils/useReactSelectCustomizations';
import type { useReactSelectValue } from './utils/useReactSelectValueWrapper';

export { SELECT_SIZES } from './constants';

export type SelectProps<
  TOption extends SelectOption<any> = SelectOption<any>,
  TIsMulti extends boolean = false,
  TGroup extends GroupBase<TOption> = GroupBase<TOption>,
  TUseOptionAsValue extends boolean = false,
> = Omit<
  ReactSelectProps<TOption, TIsMulti, TGroup>,
  'isDisabled' | 'formatOptionLabel' | 'value' | 'onChange'
> &
  CommonSelectProps & {
    useOptionAsValue?: TUseOptionAsValue;
    value?: Parameters<
      typeof useReactSelectValue<TOption, TGroup, TIsMulti, TUseOptionAsValue>
    >[0]['value'];
    onChange?: Parameters<
      typeof useReactSelectValue<TOption, TGroup, TIsMulti, TUseOptionAsValue>
    >[0]['onChange'];
    'data-cy'?: string;
  };

const SelectInner = <
  TOption extends SelectOption<any>,
  TIsMulti extends boolean,
  TGroup extends GroupBase<TOption>,
  TUseOptionAsValue extends boolean,
>(
  {
    size = SELECT_SIZES.MEDIUM,
    disabled = false,
    hasError = false,
    hasWarning = false,
    id = null,
    useOptionAsValue,
    'data-cy': dataCy,
    noOptionsMessage = NO_OPTIONS_MESSAGE,
    placeholder = PLACEHOLDER_DEFAULT_VALUE,
    loadingMessage = LOADING_MESSAGE,
    ...props
  }: SelectProps<TOption, TIsMulti, TGroup, TUseOptionAsValue>,
  ref: ForwardedRef<SelectInstance<TOption, TIsMulti, TGroup>>,
) => {
  const { onChange, onChangeMulti, ...customProps } = useReactSelectCustomization<
    TOption,
    TIsMulti,
    TGroup,
    TUseOptionAsValue
  >({
    hasError,
    hasWarning,
    size,
    useOptionAsValue,
    isMulti: props.isMulti,
    value: props.value,
    onChange: props.onChange,
    options: props.options,
    filterOption: props.filterOption,
    menuPortalTarget: props.menuPortalTarget,
  });

  const customComponentsWithDataCy = {
    Control: (controlProps: any) => (
      <components.Control
        {...controlProps}
        innerProps={{
          ...controlProps.innerProps,
          'data-cy': dataCy,
        }}
      />
    ),
  };
  return (
    <ReactSelect<TOption, TIsMulti, TGroup>
      {...props}
      {...customProps}
      ref={ref}
      inputId={id}
      components={{ ...customComponentsWithDataCy, ...props.components }}
      aria-labelledby={id}
      onChange={props.isMulti ? onChangeMulti : onChange}
      isDisabled={disabled}
      noOptionsMessage={noOptionsMessage}
      placeholder={placeholder}
      loadingMessage={loadingMessage}
    />
  );
};

export const SelectBase = forwardRef(SelectInner) as {
  <
    TOption extends SelectOption<any> = SelectOption<any>,
    TIsMulti extends boolean = false,
    TGroup extends GroupBase<TOption> = GroupBase<TOption>,
    TUseOptionAsValue extends boolean = false,
  >(
    props: SelectProps<TOption, TIsMulti, TGroup, TUseOptionAsValue> & {
      ref?: ForwardedRef<SelectInstance<TOption, TIsMulti, TGroup>>;
    },
  ): ReactElement | null;

  displayName?: any;
};

SelectBase.displayName = 'Select';

export const Select = memo(SelectBase) as typeof SelectBase & {
  SIZES?: typeof SELECT_SIZES;
};

Select.SIZES = SELECT_SIZES;
